import { ChangeEvent, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useForm } from "react-hook-form";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { defaultManutencaoForm, ManutencaoForm, ManutencaoSchema } from "../schemas/Manutencao/manutencao";
import { ManutencaoModel } from "../../domain/models/manutencao_model";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { FileUtils } from "../../../../common/utils/file_utils";

const convertStatusStringToNumber = (status: string) => {
  switch (status) {
    case 'E':
      return 0;
    case 'A':
      return 1;
    case 'R':
      return 2;
    case 'F':
      return 3;
    default:
      return 0;
  }
}

const convertStatusNumberToString = (status: number) => {
  switch (status) {
    case 0:
      return 'E';
    case 1:
      return 'A';
    case 2:
      return 'R';
    case 3:
      return 'F';
    default:
      return 'E';
  }
}

export const useManutencaoGerenciarCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    controlePatrimonio: {
      dropDownMaquinaEquipUseCase,
      getManutencaoByIdUseCase,
      updateManutencaoUseCase,
      createManutencaoUseCase,
      getManutencaoGerenciarArquivoByIdUseCase,
    },
  } = useDependencyInjection();

  const location = useLocation();
  const navigate = useNavigate();

  const { pushErrorNotification, pushInfoNotification, setCustomLoading } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  //ARQUIVOS
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [nomeArquivo, setNomeArquivo] = useState<string>();
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [selectedManutencao, setSelectedManutencao] = useState<ManutencaoModel>();
  const [ddlMaquinaEquip, setDdlMaquinaEquip] = useState<DropDown[]>([]);

  const hookForm = useForm<ManutencaoForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultManutencaoForm,
    resolver: zodResolver(ManutencaoSchema),
  });

  const selectedStatus = hookForm.watch("status");
  const selectedTipo = hookForm.watch("tipoManutencao");

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  //#region CARREGAMENTO 

  const loadDropdownMaquinaEquip = useCallback(async () => {
    try {
      setLoading(true);
      const result = await dropDownMaquinaEquipUseCase.dropDownMaquinaEquip();
      setDdlMaquinaEquip(result.map(item => ({ id: item.idMaquinaEquip, title: item.descricao })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar máquinas/equipamentos!"));
      setDdlMaquinaEquip([]);
    } finally {
      setLoading(false);
    }
  }, [
    dropDownMaquinaEquipUseCase,
    pushErrorNotification
  ]);

  const loadManutencaoById = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getManutencaoByIdUseCase.execute(id);
        setSelectedManutencao(result);
        hookForm.setValue("idManutencao", result.idManutencao);
        hookForm.setValue("status", convertStatusStringToNumber(result.status));
        hookForm.setValue("nroManutencao", result.nroManutencao);
        hookForm.setValue("tipoManutencao", result.tipoManutencao ?? 1);
        hookForm.setValue("idMaquinaEquip", result.idMaquinaEquip);
        hookForm.setValue("idCentroCusto", result.idCentroCusto);
        hookForm.setValue("idFuncionarioSolicitante", result.idFuncionarioSolicitante);
        hookForm.setValue("idFuncionarioAprovacao", result.idFuncionarioAprovacao);
        hookForm.setValue("idFornecedorResponsavel", result.idFornecedorResponsavel);
        hookForm.setValue("idFuncionarioResponsavel", result.idFuncionarioResponsavel);
        hookForm.setValue("valorOrcamento", result.valorOrcamento);
        hookForm.setValue("dataPrevConclusao", result.dataPrevConclusao);
        hookForm.setValue("dataConclusao", result.dataConclusao);
        hookForm.setValue("dataAprovacao", result.dataAprovacao);
        hookForm.setValue("descricao", result.descricao ?? undefined);
        hookForm.setValue("observacao", result.observacao ?? undefined);
        hookForm.setValue("motivoReprovacao", result.motivoReprovacao ?? undefined);
        hookForm.setValue("dataSolicitacao", result.dataSolicitacao ? new Date(result.dataSolicitacao) : undefined);
        hookForm.setValue("nomeArquivo1", result.nomeArquivo1 ?? undefined);
        hookForm.setValue("nomeArquivo2", result.nomeArquivo2 ?? undefined);
        hookForm.setValue("nomeArquivo3", result.nomeArquivo3 ?? undefined);

      } catch (e: unknown) {
        setErrorMessage(processError(e, "Falha ao carregar revisão")[0]);
      } finally {
        setLoading(false);
      }
    },
    [
      hookForm,
      getManutencaoByIdUseCase,
    ],
  );

  //#endregion



  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }

      await loadManutencaoById(Number(locationId));
      await loadDropdownMaquinaEquip();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

    }
  });


  //#endregion



  //#region CREATE/UPDATE


  const handleSubmitManutencao = useCallback(async (form: ManutencaoForm) => {
    try {

      setSaving(true);
      if (form.arquivo1 !== null) FileUtils.validateFileSize(form.arquivo1);
      if (form.arquivo2 !== null) FileUtils.validateFileSize(form.arquivo2);
      if (form.arquivo3 !== null) FileUtils.validateFileSize(form.arquivo3);

      if (selectedManutencao) {
        await updateManutencaoUseCase.execute(
          form.idManutencao ?? 0,
          convertStatusNumberToString(form.status),
          form.idCentroCusto ?? undefined,
          form.idFuncionarioAprovacao ?? undefined,
          form.dataPrevConclusao ?? undefined,
          form.dataConclusao ?? undefined,
          form.dataAprovacao ?? undefined,
          form.observacao ?? undefined,
          form.idMaquinaEquip ?? undefined,
          form.idFuncionarioSolicitante ?? undefined,
          form.idFornecedorResponsavel ?? undefined,
          form.valorOrcamento ?? undefined,
          form.descricao ?? undefined,
          form.dataSolicitacao ?? undefined,
          form.idFuncionarioResponsavel ?? undefined,
          form.tipoManutencao ?? undefined,
          form.motivoReprovacao ?? undefined,
          form.nomeArquivo1 ?? undefined,
          form.nomeArquivo2 ?? undefined,
          form.nomeArquivo3 ?? undefined,
          form.arquivo1 ?? undefined,
          form.arquivo2 ?? undefined,
          form.arquivo3 ?? undefined,
          form.limparArquivo1 ?? false,
          form.limparArquivo2 ?? false,
          form.limparArquivo3 ?? false
        );

        await loadManutencaoById(selectedManutencao.idManutencao);
        pushInfoNotification("Sucesso", "Dados salvos com sucesso", { isAutoFinish: true });
      } else {

        const idManutencao = await createManutencaoUseCase.execute(
          form.nroManutencao ?? 0,
          form.idManutencao ?? 0,
          convertStatusNumberToString(form.status),
          form.idCentroCusto ?? undefined,
          form.idMaquinaEquip ?? undefined,
          form.idFuncionarioSolicitante ?? undefined,
          form.idFuncionarioAprovacao ?? undefined,
          form.idFornecedorResponsavel ?? undefined,
          form.valorOrcamento ?? undefined,
          form.dataPrevConclusao ?? undefined,
          form.dataConclusao ?? undefined,
          form.dataAprovacao ?? undefined,
          form.descricao ?? undefined,
          form.observacao ?? undefined,
          form.dataSolicitacao ?? undefined,
          form.nomeArquivo1 ?? undefined,
          form.nomeArquivo2 ?? undefined,
          form.nomeArquivo3 ?? undefined,
          form.arquivo1 ?? undefined,
          form.arquivo2 ?? undefined,
          form.arquivo3 ?? undefined,
        );

        await loadManutencaoById(idManutencao);
        pushInfoNotification("Sucesso", "Manutenção criada com sucesso", { isAutoFinish: true });
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar revisão!")[0]);
    } finally {
      setSaving(false);
    }
  }, [
    selectedManutencao,
    updateManutencaoUseCase,
    createManutencaoUseCase,
    loadManutencaoById,
    pushInfoNotification,
  ]);

  //#endregion


  //#region ARQUIVOS

  const handleDownload = useCallback(async (nroArquivo: number, imageName: string) => {
    if (!saving && selectedManutencao) {
      try {
        setCustomLoading(true, "Preparando download...");
        await getManutencaoGerenciarArquivoByIdUseCase.execute(selectedManutencao.idManutencao, nroArquivo, imageName);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    };
  }, [
    saving,
    selectedManutencao,
    getManutencaoGerenciarArquivoByIdUseCase,
    setCustomLoading,
    pushErrorNotification,
  ]);

  const handleArquivoDeleteOpenState = useCallback((limparArquivos: string | null, nomeArquivo: string | null, openState: boolean) => {
    setOpenDeleteArquivoModal(openState)
    if (openState && limparArquivos && nomeArquivo) {
      setLimparArquivo(limparArquivos)
      setNomeArquivo(nomeArquivo)
    } else {
      setLimparArquivo(undefined)
      setNomeArquivo(undefined)
    }
  }, []);

  const handleDeleteFile = useCallback((limparArquivos: string, nomeArquivo: string) => {
    if (!saving) {
      hookForm.setValue(limparArquivos as any, true);
      hookForm.setValue(nomeArquivo as any, null);
      setSelectedManutencao(prev => prev ? ({ ...prev, [nomeArquivo]: null }) : undefined)
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleConfirmarDeletarArquivo = useCallback(async () => {
    try {
      if (limparArquivo && nomeArquivo) {
        handleDeleteFile(limparArquivo, nomeArquivo);
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao deletar arquivo!"), { isAutoFinish: true });
    } finally {
      setOpenDeleteArquivoModal(false)
      setNomeArquivo(undefined)
      setLimparArquivo(undefined)
    }

  }, [
    limparArquivo,
    nomeArquivo,
    pushErrorNotification,
    handleDeleteFile]);

  const handleFileChange = useCallback((nomeArquivo: string, limparArquivos: string, e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving) {
      hookForm.setValue(limparArquivos as any, false);
      hookForm.setValue(nomeArquivo as any, e.target.files?.item(0)?.name ?? '');
      onChange(e.target.files?.item(0));
    }
  }, [
    saving,
    hookForm,
  ]);

  //#endregion


  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/ManutencaoGerenciar_lista');
  }, [navigate]);


  const handleReloadPage = () => {
    hookForm.reset();
    const params = new URLSearchParams(location.search);
    const locationId = params.get("id");
    if (locationId) loadManutencaoById(Number(locationId));

  };

  //#endregion

  //#region  Historico

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (selectedManutencao) {
          changeHistory.handleOpenChangeHistory(selectedManutencao.idManutencao, LogTipoTabela.MANUTENCAO, false);
        }
      }
    },
    [changeHistory, selectedManutencao],
  );

  //#endregion

  return {
    loading,
    selectedManutencao,
    hookForm,
    errorMessage,
    saving,
    changeHistory,
    ddlMaquinaEquip,
    selectedStatus,
    selectedTipo,
    openDeleteArquivoModal,

    handleDownload,
    handleArquivoDeleteOpenState,
    handleConfirmarDeletarArquivo,
    handleFileChange,
    handleOpenChangeHistory,
    handleSubmitManutencao: hookForm.handleSubmit(handleSubmitManutencao),
    handlePageBack,
    handleReloadPage,
  };
};
